import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { AccountInfo, AuthenticationResult, StringUtils } from '@azure/msal-browser';
import { Inject, Injectable } from '@angular/core';
import { MSAL_INTERCEPTOR_CONFIG, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { BrowserConfigurationAuthError, InteractionType } from '@azure/msal-browser';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class HttpHeaderInterceptor implements HttpInterceptor {
  constructor(
    @Inject(MSAL_INTERCEPTOR_CONFIG)
    private msalInterceptorConfig: MsalInterceptorConfiguration,
    private authService: MsalService
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (
      this.msalInterceptorConfig.interactionType !== InteractionType.Redirect
    ) {
      throw new BrowserConfigurationAuthError(
        'invalid_interaction_type',
        'Invalid interaction type provided to MSAL Interceptor. InteractionType.Popup, InteractionType.Redirect must be provided in the msalInterceptorConfiguration'
      );
    }
    this.authService.getLogger().verbose('MSAL Interceptor activated');
    const scopes = this.getScopesForEndpoint();

    // Sets account as active account or first account
    let account: AccountInfo;
    if (!!this.authService.instance.getActiveAccount()) {
      this.authService.getLogger().verbose('Interceptor - active account selected');
      account = this.authService.instance.getActiveAccount();
    } else {
      this.authService.getLogger().verbose('Interceptor - no active account, fallback to first account');
      account = this.authService.instance.getAllAccounts()[0];
    }

    this.authService.getLogger().verbose('Interceptor - no scopes for endpoint');

    return this.authService.acquireTokenSilent({ ...this.msalInterceptorConfig.authRequest, scopes, account })
      .pipe(
        catchError(() => {
          this.authService.getLogger().error('Interceptor - acquireTokenSilent rejected with error. Invoking interaction to resolve.');
          return this.invokingLogoutToAcquireToken();
        }),
        switchMap((result: AuthenticationResult) => {
          if (!result.idToken) {
            this.authService.getLogger().error('Interceptor - acquireTokenSilent resolved with null access token. Known issue with B2C tenants, invoking interaction to resolve.');
            return this.invokingLogoutToAcquireToken();
          }
          if (this.isTokenExpired(result.idTokenClaims)) {
            return this.authService.acquireTokenRedirect({ ...this.msalInterceptorConfig.authRequest, scopes, account });
          }
          return of(result);
        }),
        switchMap((result: AuthenticationResult) => {
         
          this.authService.getLogger().verbose('Interceptor - setting authorization headers');
          localStorage.setItem('msal.idtoken', result.idToken);
          if (
            req.urlWithParams.indexOf('localhost') > -1 || req.urlWithParams.indexOf('lyra') > -1
          ) {
            req = req.clone({
              setHeaders: {
                Authorization: 'Bearer ' + localStorage.getItem('msal.idtoken')
              }
            });
          }
          return next.handle(req);
        })
      );

  }

  /**
   * Invoke logout for the given set of scopes
   * @returns Result from the interactive request
   */
  private invokingLogoutToAcquireToken(): Observable<AuthenticationResult> {
    this.authService.getLogger().verbose('Interceptor - error acquiring token silently, redirect logout on application');
    this.authService.logout();
    return EMPTY;
  }


  /**
   * Looks up the scopes for the given endpoint from the protectedResourceMap
   * @param endpoint Url of the request
   * @returns Array of scopes, or null if not found
   *
   */
  private getScopesForEndpoint(): Array<string> | any {
    this.authService.getLogger().verbose('Interceptor - getting scopes for endpoint');
    const protectedResourcesArray = Array.from(this.msalInterceptorConfig.protectedResourceMap.keys());

    // Process all protected resources and send the first matched resource
    if (protectedResourcesArray.length > 0) {
      const keyForEndpoint = protectedResourcesArray[0];
      if (keyForEndpoint) {
        return this.msalInterceptorConfig.protectedResourceMap.get(keyForEndpoint);
      }
    }

    return null;
  }

  private isTokenExpired = (expiry) => {
    const { exp } = expiry;
    return (Math.floor((new Date).getTime() / 1000)) >= exp;
  }

}
